/**
 * API key permission scopes ("scopes") and per-subject overrides ("grants").
 *
 * Grammar:
 * - `*` grants every action on every family.
 * - `<family>.*` grants every action on one family.
 * - `<family>.<action>` with action levels write ⊃ read; `write` covers
 *   create/update/delete and implies read.
 *
 * Families are prefixed with the API surface their routes live on —
 * `gateway.` for Event Gateway routes, `outpost.` for Outpost routes —
 * so the family itself may contain a dot (`gateway.events`). Platform
 * families (organizations, projects, api-keys) are unprefixed. The
 * action is always the segment after the LAST dot.
 *
 * Grants are object-specific scope overrides, one level deep:
 * - organization keys: keyed by project (team) id — never resource-deep
 * - project keys: keyed by resource id (source, destination, connection, ...);
 *   no longer accepted by the API, but keys created with them keep them
 * Effective scopes acting on a subject = grants[subject]?.scopes ?? root scopes.
 *
 * The stored grant scopes of organization keys are DERIVED, not client-set:
 * the external API takes the full scope list at the root plus a bare list of
 * granted projects, and splitScopesAcrossProjectGrants computes the stored
 * split (per-project scopes per grant are reserved for a future version).
 */

export const scope_actions = ['read', 'write'] as const;
export type ScopeAction = (typeof scope_actions)[number];

export const scope_families = [
  // Platform surface (unprefixed)
  'organizations',
  'projects',
  'api-keys',
  // Event Gateway surface
  'gateway.connections',
  'gateway.sources',
  'gateway.destinations',
  'gateway.transformations',
  'gateway.issue-triggers',
  'gateway.bookmarks',
  'gateway.issues',
  'gateway.events',
  'gateway.requests',
  'gateway.attempts',
  'gateway.metrics',
  // Publishing through the Publish API (edge ingestion). Keys holding
  // `gateway.publish.write` anywhere (root scopes or any grant) are synced to
  // the Cloudflare KV that backs edge authentication; other keys are not.
  'gateway.publish',
  // Outpost surface
  'outpost.tenants',
  'outpost.destinations',
  'outpost.events',
  'outpost.attempts',
  'outpost.metrics',
] as const;
export type ScopeFamily = (typeof scope_families)[number];

export type ApiKeyRequiredScope = `${ScopeFamily}.${ScopeAction}`;
export type ApiKeyScope = '*' | `${ScopeFamily}.*` | ApiKeyRequiredScope;

export interface ApiKeyGrant {
  scopes: ApiKeyScope[];
}

export type ApiKeyGrants = Record<string, ApiKeyGrant>;

/**
 * External create/update shape of a grant (v1): grants name the projects an
 * organization key is limited to, `true` or `{}` per project — its scope list
 * always arrives in the root `scopes` property. A project key is already
 * limited to its own project, so grants don't apply to it. The object form is
 * what a future per-project scope override will extend.
 */
export type ApiKeyGrantInput = true | { scopes?: ApiKeyScope[] };

export type ApiKeyGrantsInput = Record<string, ApiKeyGrantInput>;

export interface ApiKeyScopeHolder {
  scopes?: string[] | null;
  grants?: ApiKeyGrants | null;
}

/** Families that only exist as read surfaces (no write routes). */
export const read_only_families: ScopeFamily[] = [
  'gateway.attempts',
  'gateway.metrics',
  'outpost.attempts',
  'outpost.metrics',
];

/** Families that operate on the organization itself rather than a project. */
export const org_surface_families: ScopeFamily[] = ['organizations', 'api-keys'];

/** Families that operate within a single project. */
export const project_surface_families: ScopeFamily[] = scope_families.filter(
  (family) => family !== 'organizations' && family !== 'api-keys' && family !== 'projects'
);

const action_levels: Record<string, number> = { read: 0, write: 1, '*': 1 };

/**
 * The family portion of a scope — everything before the action (the segment
 * after the last dot). Surface-prefixed families keep their prefix:
 * `gateway.events.read` → `gateway.events`. Dot-less values (`*`) are
 * returned as-is.
 */
export function scopeFamily(scope: string): string {
  const separator = scope.lastIndexOf('.');
  return separator === -1 ? scope : scope.slice(0, separator);
}

/** The action portion of a scope (the segment after the last dot). */
export function scopeAction(scope: string): string {
  return scope.slice(scope.lastIndexOf('.') + 1);
}

export function isValidScope(value: string): value is ApiKeyScope {
  if (value === '*') {
    return true;
  }
  const family = scopeFamily(value);
  const action = scopeAction(value);
  return (
    (scope_families as readonly string[]).includes(family) &&
    (action === '*' || (scope_actions as readonly string[]).includes(action))
  );
}

/**
 * Find the first pair of scopes targeting the same family (e.g. `gateway.issues.read`
 * and `gateway.issues.write`). Action levels are hierarchical, so a scope list holds at
 * most one entry per family; duplicates included. Returns the offending pair.
 */
export function findConflictingScopes(scopes: string[]): [string, string] | undefined {
  const scope_by_family = new Map<string, string>();
  for (const scope of scopes) {
    const family = scopeFamily(scope);
    const held_scope = scope_by_family.get(family);
    if (held_scope !== undefined) {
      return [held_scope, scope];
    }
    scope_by_family.set(family, scope);
  }
  return undefined;
}

/**
 * Whether a held scope list satisfies a required `family.action` scope.
 * Unknown or malformed held entries never match.
 */
export function scopeSatisfies(required: ApiKeyRequiredScope, held_scopes: string[]): boolean {
  const required_family = scopeFamily(required);
  const required_action = scopeAction(required);
  return held_scopes.some((held) => {
    if (held === '*') {
      return true;
    }
    return (
      scopeFamily(held) === required_family &&
      action_levels[scopeAction(held)] !== undefined &&
      action_levels[scopeAction(held)] >= action_levels[required_action]
    );
  });
}

/**
 * The requested scopes a held scope list does not cover. Delegation never
 * exceeds the delegating key: `*` needs `*`; `family.*` and `family.write`
 * need write (or a wildcard) on the family; `family.read` needs read or better.
 */
export function uncoveredScopes(requested: string[], held_scopes: string[]): string[] {
  return requested.filter((scope) => {
    if (scope === '*') {
      return !held_scopes.includes('*');
    }
    const action = scopeAction(scope) === '*' ? 'write' : scopeAction(scope);
    return !scopeSatisfies(`${scopeFamily(scope)}.${action}` as ApiKeyRequiredScope, held_scopes);
  });
}

/**
 * Resolve the scopes in effect for a key acting on an optional subject.
 * A grant for the subject overrides the root scopes entirely; keys predating the
 * scopes migration (no `scopes` value) are treated as full access.
 */
export function effectiveApiKeyScopes(
  api_key: ApiKeyScopeHolder,
  subject_id?: string | null
): string[] {
  if (subject_id && api_key.grants?.[subject_id]) {
    return api_key.grants[subject_id].scopes;
  }
  return api_key.scopes ?? ['*'];
}

export function apiKeyHasScope(
  api_key: ApiKeyScopeHolder,
  required: ApiKeyRequiredScope,
  subject_id?: string | null
): boolean {
  return scopeSatisfies(required, effectiveApiKeyScopes(api_key, subject_id));
}

/**
 * Whether a key holds full access (`*`) within a project. Organization keys are
 * evaluated on the project's grant, else their root scopes. Project keys need
 * `*` at the root and in every per-resource grant, since a grant narrows access
 * to its resource. Used before handing out credentials that are not themselves
 * scope-checked.
 */
export function apiKeyHasFullProjectAccess(
  api_key: ApiKeyScopeHolder & { team_id?: string | null },
  project_id: string
): boolean {
  if (!api_key.team_id) {
    return isFullAccess(effectiveApiKeyScopes(api_key, project_id));
  }
  return (
    api_key.team_id === project_id &&
    isFullAccess(effectiveApiKeyScopes(api_key)) &&
    Object.values(api_key.grants ?? {}).every((grant) => isFullAccess(grant.scopes))
  );
}

/** The scope required to publish through the Publish API. */
export const PUBLISH_API_SCOPE: ApiKeyRequiredScope = 'gateway.publish.write';

/**
 * Whether any of the key's contexts — root scopes or any per-subject grant —
 * satisfies the required scope. Used where the subject cannot be resolved
 * upfront, e.g. deciding whether a key may reach the Publish API at all
 * (and therefore whether it belongs in the edge KV).
 */
export function apiKeyHasScopeAnywhere(
  api_key: ApiKeyScopeHolder,
  required: ApiKeyRequiredScope
): boolean {
  if (scopeSatisfies(required, api_key.scopes ?? ['*'])) {
    return true;
  }
  return Object.values(api_key.grants ?? {}).some((grant) =>
    scopeSatisfies(required, grant.scopes)
  );
}

/**
 * Merge every grant's scope list into one, keeping the highest action level
 * per family. Used to derive the grant for a subject the key itself created:
 * the key gets on the new subject what it already holds elsewhere.
 */
export function mergedGrantScopes(grants: ApiKeyGrants): ApiKeyScope[] {
  const all_scopes = Object.values(grants).flatMap((grant) => grant.scopes);
  if (all_scopes.includes('*')) {
    return ['*'];
  }

  const action_by_family = new Map<string, string>();
  for (const scope of all_scopes) {
    const family = scopeFamily(scope);
    const action = scopeAction(scope);
    const held_action = action_by_family.get(family);
    if (
      held_action === undefined ||
      (action_levels[action] ?? -1) > (action_levels[held_action] ?? -1)
    ) {
      action_by_family.set(family, action);
    }
  }

  return [...action_by_family].map(([family, action]) => `${family}.${action}` as ApiKeyScope);
}

/**
 * Stored shape of an organization key restricted to specific projects.
 * Externally the full scope list lives at the root and grants only name the
 * granted projects; internally each granted project receives the
 * project-reaching scopes and the root keeps only organization-surface ones,
 * so the deny-by-default root keeps every other project out of reach.
 */
export function splitScopesAcrossProjectGrants(
  scopes: ApiKeyScope[],
  project_ids: string[]
): { scopes: ApiKeyScope[]; grants: ApiKeyGrants } {
  const reachesOrgSurface = (scope: ApiKeyScope) =>
    org_surface_families.includes(scopeFamily(scope) as ScopeFamily);
  const full_access = isFullAccess(scopes);
  const root_scopes = full_access ? [] : scopes.filter(reachesOrgSurface);
  const grant_scopes: ApiKeyScope[] = full_access
    ? ['*']
    : scopes.filter((scope) => !reachesOrgSurface(scope));
  return {
    scopes: root_scopes,
    grants: Object.fromEntries(
      project_ids.map((project_id) => [project_id, { scopes: grant_scopes }])
    ),
  };
}

/**
 * Reverse of splitScopesAcrossProjectGrants: the external, root-level view of
 * a key's scope list, with grant scopes folded back in at the highest action
 * level per family.
 */
export function combinedApiKeyScopes(api_key: ApiKeyScopeHolder): ApiKeyScope[] {
  const root_scopes = (api_key.scopes ?? ['*']) as ApiKeyScope[];
  const grants = api_key.grants ?? {};
  if (Object.keys(grants).length === 0) {
    return root_scopes;
  }
  return mergedGrantScopes({ ...grants, __root__: { scopes: root_scopes } });
}

export const full_access_scopes: ApiKeyScope[] = ['*'];

export function isFullAccess(scopes: string[]): boolean {
  return scopes.includes('*');
}

/**
 * Read-only preset: read on every family except `api-keys` (listing API keys
 * exposes key material, so it stays an explicit opt-in).
 */
export function readOnlyScopes(): ApiKeyScope[] {
  return scope_families
    .filter((family) => family !== 'api-keys')
    .map((family) => `${family}.read` as ApiKeyScope);
}
